import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import NavTitle from '../../../assets/svg/nav-title.svg';
import { pageInfo } from '../../../constants/pageInfo/pageInfo.constant';
import { useAppDispatch, useAppSelector } from '../../../hooks/store/useStore';
import { useCheckUserLoggedIn } from '../../../hooks/user/useCheckUserLoggedIn';
import { firstPageSlice } from '../../../store/firstPageSlice';
import { Icon } from '../icon/Icon';

interface MainHeadProps {
  hasPluvaLogo?: boolean;
  backButton: {
    isShow: boolean;
    directlyPassedURL?: string;
  };
  hasMyInfoButton?: boolean;
  hasSettingButton?: boolean;
  headerTitle?: string;
}

export const MainHead = ({
  hasPluvaLogo = false,
  backButton,
  hasMyInfoButton = false,
  hasSettingButton = false,
  headerTitle,
}: MainHeadProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isUserLoggedIn } = useCheckUserLoggedIn();

  const dispatch = useAppDispatch();
  const firstPageStates = useAppSelector((state) => state.firstPage);
  const firstPageActions = firstPageSlice.actions;

  useEffect(() => {
    if (backButton.isShow && firstPageStates.isFirstPageHasBackButton === false && location.key === 'default') {
      dispatch(firstPageActions.setIsFirstPageHasBackButton(true));
    }
  }, [backButton.isShow, firstPageStates.isFirstPageHasBackButton, location.key]);

  const handleBackButtonClick = () => {
    // 최초 진입 페이지가 back button이 없었다면 (정상적인 상황, 브라우저 히스토리를 그대로 이용해도 됨)
    if (firstPageStates.isFirstPageHasBackButton === false) {
      navigate(-1);
    }

    // 최초 진입 페이지가 back button이 있었다면 (예외적인 상황, 브라우저 히스토리를 그대로 이용하면 외부 페이지로 갈 수도 있으므로 이용하면 안됨)
    else {
      // 페이지를 이동하기 전에, 브라우저 히스토리를 이용할 수 있는 정상적인 상황으로 돌아가므로 전역 상태 변경
      dispatch(firstPageActions.setIsFirstPageHasBackButton(false));

      // 직접 지정한 페이지가 있다면 해당 페이지로 이동
      if (backButton.isShow && backButton.directlyPassedURL) {
        navigate(backButton.directlyPassedURL);
      }

      // 직접 지정한 페이지가 없다면 홈 페이지로 이동
      else {
        navigate(pageInfo.homePage.path);
      }
    }
  };

  return (
    <Container>
      <LeftSection>
        {hasPluvaLogo && <PluvaLogo src={NavTitle} alt={'Pluva-Market-Icon'} />}
        {backButton.isShow && (
          <BackButton onClick={handleBackButtonClick}>
            <Icon name={'ArrowLeft'} width={20} height={20} />
          </BackButton>
        )}
      </LeftSection>

      <CenterSection>
        <HeaderTitle>{headerTitle}</HeaderTitle>
      </CenterSection>

      <RightSection>
        {hasMyInfoButton && (
          <MyInfoButton
            onClick={() => {
              if (isUserLoggedIn) {
                navigate('/myinfo');
              } else {
                navigate('/oauth/login');
              }
            }}
          >
            <Icon name={'Person'} width={24} height={24} />
          </MyInfoButton>
        )}
        {hasSettingButton && (
          <button onClick={() => navigate('/myinfo/setting')}>
            <Icon name={'Setting'} />
          </button>
        )}
      </RightSection>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 40px;

  padding: 0px 16px;

  background-color: #ffffff;
`;

const PluvaLogo = styled.img`
  width: 64px;
  height: 15.646px;

  margin: 14px 0px;
`;

const LeftSection = styled.div`
  width: 20%;

  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
`;

const RightSection = styled.div`
  width: 20%;

  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
`;

const CenterSection = styled.div`
  width: 60%;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
`;

const HeaderTitle = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);
  text-align: center;

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */

  overflow: hidden;
  text-overflow: ellipsis;

  display: block;
  white-space: nowrap;
`;

const BackButton = styled.button``;

const MyInfoButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
`;
